import React, { useEffect, useState } from 'react';
import {
  Form,
  Row,
  Col,
  Select,
  Input,
  InputNumber,
  Button,
  Divider,
  Typography,
  Spin,
  Collapse,
  Space
} from 'antd';

import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { debounce } from '../Common/Debounce';
import { getUnits } from '../../services/unitService';
import {
  getCategories,
  formatCategories
} from '../../services/courseCategoriesService';
import { getCourses } from '../../services/courseService';
import { formatUnits } from '../../utilities/unitFormat';
import {
  COURSE_TYPES,
  courseAvailableDomains,
  customCards,
  cusotmLiveSessionCards,
  nullCourseOption
} from '../../utilities/constants';
import ImageUploadSection from '../ImageUploadSection';
import { beforeImageUpload } from '../../utilities/imageUpload';
import NotedFormItem from '../Common/NotedFormItem';
const { Panel } = Collapse;

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 4 }
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 20 }
  }
};
const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 20, offset: 4 }
  }
};

const { Text } = Typography;

const CourseForm = ({
  initialValues,
  onSubmit,
  status,
  type,
  setStatus,
  feedbackTemplates,
  currentUnits
}) => {
  const [fetching, setFetching] = useState(false);
  const [form] = Form.useForm();
  const [courses, setCourses] = useState([]);
  const [units, setUnits] = useState([]);
  const [courseCode, setCourseCode] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCustomCards, setSelectedCustomCards] = useState({});
  const [chosenSubCategories, setChosenSubCategories] = useState({});

  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const fetchUnitHandler = debounce((value) => {
    setFetching(true);
    getUnits(1, 20, { unitSearch: value })
      .then((response) => {
        const units = response.data.paginatedList;
        const data = formatUnits(units);
        setUnits(data);
        setFetching(false);
      })
      .catch((error) => {
        if (error?.response?.data?.message)
          setStatus({
            type: 'ERROR',
            message: error.response.data.message
          });
        else
          setStatus({
            type: 'ERROR',
            message: 'Getting questions from the backend failed!'
          });
      });
  }, 250);

  const fetchCategories = async () => {
    getCategories()
      .then((response) => {
        const categoriesFormatted = formatCategories(response.data);
        setCategories(categoriesFormatted);
      })
      .catch((error) => {
        if (error?.response?.data?.message)
          setStatus({
            type: 'ERROR',
            message: error.response.data.message
          });
        else
          setStatus({
            type: 'ERROR',
            message: 'Getting categories from the backend failed!'
          });
      });
  };

  const fetchCourses = async () => {
    getCourses()
      .then((response) => {
        const courseList = response.data.paginatedList;
        let data = [nullCourseOption];
        courseList.forEach((course) => {
          data.push({
            label: course.code,
            value: course.code,
            description: course.description,
            customCardMetadata: course.customCardMetadata
          });
        });

        setCourses(data);
      })
      .catch((error) => {
        console.log('Error getting data from the admin backend:', error);
        alert('Getting data from the backend failed!');
      });
  };

  useEffect(() => {
    fetchCategories();
    fetchCourses();
  }, []);

  // Check if unit is existing unit (Not added in current edit session) and return details
  // so user can Identify what Unit it is as Only ID is displayed. Only in UPDATE mode
  const getUnitLabelText = (index) => {
    const selectedUnit = currentUnits.filter(
      (unit) => unit._id === form.getFieldValue(['units', index])
    );

    return selectedUnit[0]
      ? `${selectedUnit[0].code} - ${selectedUnit[0].description}`
      : '';
  };

  return (
    <div className="course-add-form">
      <Form
        labelCol={{ span: 7 }}
        onFinish={onSubmit}
        initialValues={initialValues}
        form={form}>
        <section>
          <Form.Item
            label="Code"
            name="code"
            rules={[
              { required: true, message: 'Please input course code!' }
            ]}>
            <Input
              onChange={(e) => {
                setCourseCode(e.target.value);
              }}
            />
          </Form.Item>
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Please input title!' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: 'Please input description!' }
            ]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="Discovery Description"
            name="discoveryDescription"
            rules={[
              {
                required: false,
                message: 'Please input discovery description!'
              }
            ]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="First suggested course"
            name="firstSuggestedCourse"
            rules={[{ required: false, message: 'Please input course!' }]}>
            {courses ? (
              <Select
                options={courses}
                placeholder={'Select first Suggested Course'}
                mode={''}
                notFoundContent={fetching ? <Spin size="small" /> : null}
                filterOption={(input, option) => {
                  return (
                    option.label
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              />
            ) : null}
          </Form.Item>
          <Form.Item
            label="Second Suggested Course"
            name="secondSuggestedCourse"
            rules={[{ required: false, message: 'Please input course!' }]}>
            {courses ? (
              <Select
                options={courses}
                placeholder={'Select first Suggested Course'}
                mode={''}
                notFoundContent={fetching ? <Spin size="small" /> : null}
                filterOption={(input, option) => {
                  return (
                    option.label
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              />
            ) : null}
          </Form.Item>
          {/* add a mode dropdown for live/ondemand */}
          <Form.Item
            label="Course Mode"
            name="mode"
            rules={[{ required: true }]}>
            <Select>
              <Select.Option value={COURSE_TYPES.COHORT_LIVE}>
                Cohort Live
              </Select.Option>
              <Select.Option value={COURSE_TYPES.COHORT_ON_DEMAND}>
                Cohort On-Demand
              </Select.Option>
              <Select.Option value={COURSE_TYPES.VIDEO_ON_DEMAND}>
                Video On-Demand
              </Select.Option>
              <Select.Option value={COURSE_TYPES.FREE_WORKSHOP}>
                Free Workshop
              </Select.Option>
            </Select>
          </Form.Item>

          {/* add a mode dropdown for live/ondemand */}
          <Form.Item
            label="Is Daily/Mastercourse?"
            name="type"
            rules={[{ required: true }]}>
            <Select>
              <Select.Option value={'daily'}>Daily</Select.Option>
              <Select.Option value={'mastercourse'}>
                Mastercourse
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Is Active?"
            name="isActive"
            rules={[{ required: true }]}>
            <Select>
              <Select.Option value={true}>True</Select.Option>
              <Select.Option value={false}>False</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Is Public?"
            name="isPublic"
            rules={[{ required: true }]}>
            <Select>
              <Select.Option value={true}>True</Select.Option>
              <Select.Option value={false}>False</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Is Offline?"
            name="isOffline"
            rules={[{ required: true }]}>
            <Select>
              <Select.Option value={true}>True</Select.Option>
              <Select.Option value={false}>False</Select.Option>
            </Select>
          </Form.Item>
          <NotedFormItem
            text={'NEW'}
            note={
              'added 9/5/2022, will prioritise for discovery page ordering'
            }>
            <Form.Item
              label="Is Prioritized?"
              name="prioritize"
              rules={[{ required: false }]}>
              <Select>
                <Select.Option value={true}>True</Select.Option>
                <Select.Option value={false}>False</Select.Option>
              </Select>
            </Form.Item>
          </NotedFormItem>
          <Form.Item
            label="Are Comments Allowed?"
            name="areCommentsAllowed"
            rules={[{ required: true }]}>
            <Select>
              <Select.Option value={true}>True</Select.Option>
              <Select.Option value={false}>False</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Are Units Unlocked?"
            name="areAllUnitsUnlocked"
            rules={[{ required: true }]}>
            <Select>
              <Select.Option value={true}>True</Select.Option>
              <Select.Option value={false}>False</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Student Count"
            name="studentCount"
            rules={[
              {
                required: true,
                message: 'Please input number of students!'
              }
            ]}>
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            label="Instructor count"
            name="instructorCount"
            rules={[
              {
                required: true,
                message: 'Please input number of instructors!'
              }
            ]}>
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            label="By"
            name="by"
            rules={[
              {
                required: false,
                message: 'Please input course taught by!'
              }
            ]}>
            <Input />
          </Form.Item>

          <Form.Item
            label="University"
            name="university"
            rules={[
              { required: false, message: 'Please input university!' }
            ]}>
            <Input />
          </Form.Item>

          <Form.Item
            label="Link"
            name="link"
            rules={[
              {
                required: false,
                message: 'Please input link!'
              }
            ]}>
            <Input />
          </Form.Item>
          <NotedFormItem
            text={'NEW'}
            note={
              'Trailer video thats displayed on suggested card - must start with {PRIVATE_VIDEO_BASE_URL}'
            }>
            <Form.Item
              label="Trailer link"
              name={['trailer', 'link']}
              rules={[
                {
                  required: false,
                  message: 'Please input trailer link!'
                }
              ]}>
              <Input />
            </Form.Item>
            <Form.Item
              label="Trailer hls link"
              name={['trailer', 'hlsLink']}
              rules={[
                {
                  required: false,
                  message: 'Please input trailer link!'
                }
              ]}>
              <Input />
            </Form.Item>
          </NotedFormItem>

          <Form.Item
            label="Search tags"
            name="tags"
            rules={[
              {
                required: false,
                message: 'Please input search tags!'
              }
            ]}>
            <Input />
          </Form.Item>

          <Form.Item label="Custom Search Dropdown Tag" name="customTag">
            <Input />
          </Form.Item>

          <section>
            <Text style={{ display: 'flex', marginBottom: 8, bold: true }}>
              Categories:
            </Text>
            <Form.List name={['categories']}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKey, ...restField }) => (
                    <>
                      <Space
                        key={key}
                        style={{ display: 'flex', marginBottom: 8 }}
                        direction="vertical">
                        <Form.Item
                          name={[name, 'category']}
                          key={fieldKey}
                          {...restField}>
                          <Select
                            placeholder="Search category"
                            notFoundContent={
                              fetching ? <Spin size="small" /> : null
                            }
                            onSelect={(value) => {
                              const chosenCategory = categories.find(
                                (category) => category.title === value
                              );
                              const subCategoryOptions = chosenCategory.subCategories.map(
                                (sc) => {
                                  return { value: sc, label: sc };
                                }
                              );
                              setChosenSubCategories({
                                ...chosenSubCategories,
                                [name]: subCategoryOptions
                              });
                            }}
                            dropdownRender={(menu) => <div>{menu}</div>}>
                            {categories?.map((item) => (
                              <Select.Option value={item.title}>
                                <Panel header={item.title} key="1"></Panel>
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>

                        <Form.Item
                          placeholder={'Subcategories'}
                          name={[name, 'subCategories']}
                          key={fieldKey}
                          {...restField}
                          default={[]}>
                          <Select
                            placeholder="Search subcategory - empty for no subcategories"
                            mode={'multiple'}
                            notFoundContent={
                              fetching ? <Spin size="small" /> : null
                            }
                            options={chosenSubCategories[name]}
                            filterOption={(input, option) =>
                              option.option
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                          />
                        </Form.Item>
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                        />
                      </Space>
                    </>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => {
                        add();
                      }}
                      block
                      icon={<PlusOutlined />}>
                      Add category
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
            <Form.Item
              label="Domain Access"
              name="domainAccess"
              required={true}>
              <Select
                options={courseAvailableDomains}
                placeholder={'Select domains'}
                mode="multiple"
                required={true}
                notFoundContent={null}
                filterOption={(input, option) =>
                  option.label
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              />
            </Form.Item>
          </section>

          <Form.Item
            label="Meet Creator Recording Link"
            name="meetCreatorRecordingLink"
            rules={[
              {
                required: false,
                message: 'Please input meet creator recording link!'
              }
            ]}>
            <Input />
          </Form.Item>
          {feedbackTemplates && (
            <Form.Item
              label="Student Feedback Template"
              name="studentFeedbackSkeletonId"
              rules={[
                { required: false, message: 'Please input a template!' }
              ]}>
              <Select
                onChange={(value) => {
                  const template = feedbackTemplates.find(
                    (feedbackTemplate) => {
                      return feedbackTemplate._id === value;
                    }
                  );
                  setSelectedFeedback(template);
                }}>
                {feedbackTemplates?.map((feedbackTemplate) => {
                  return (
                    <Select.Option
                      key={feedbackTemplate._id}
                      value={feedbackTemplate._id}>
                      {feedbackTemplate.name}
                    </Select.Option>
                  );
                }) ?? null}
              </Select>
            </Form.Item>
          )}
          {feedbackTemplates && (
            <Form.Item
              label="Trainer Feedback Template"
              name="trainerFeedbackSkeletonId"
              rules={[
                { required: false, message: 'Please input a template!' }
              ]}>
              <Select
                onChange={(value) => {
                  const template = feedbackTemplates.find(
                    (feedbackTemplate) => {
                      return feedbackTemplate._id === value;
                    }
                  );
                  setSelectedFeedback(template);
                }}>
                {feedbackTemplates?.map((feedbackTemplate) => {
                  return (
                    <Select.Option
                      key={feedbackTemplate._id}
                      value={feedbackTemplate._id}>
                      {feedbackTemplate.name}
                    </Select.Option>
                  );
                }) ?? null}
              </Select>
            </Form.Item>
          )}
        </section>

        <Divider />
        <section className="course-add-unit-section">
          <Text strong="true">Units</Text>

          <Form.List
            name={['units']}
            label="units"
            rules={[{ required: false, message: 'Please add units' }]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <>
                    <div
                      key={field.key}
                      className={`${index}`}
                      style={{ display: 'flex', marginBottom: '10px' }}>
                      <Form.Item
                        {...field}
                        {...(index === 0
                          ? formItemLayout
                          : formItemLayoutWithOutLabel)}
                        label={index === 0 ? 'Unit' : ''}
                        style={{ margin: '5px', width: '90%' }}
                        fieldKey={[field.fieldKey, '_id']}
                        rules={[
                          { required: false, message: 'Missing unit' }
                        ]}>
                        <Select
                          mode={''}
                          placeholder="Search units"
                          notFoundContent={
                            fetching ? <Spin size="small" /> : null
                          }
                          filterOption={false}
                          showSearch={true}
                          onSearch={fetchUnitHandler}
                          dropdownRender={(menu) => <div>{menu}</div>}>
                          {units?.map((item) => (
                            <Select.Option value={item.value}>
                              <Collapse>
                                <Panel header={item.label} key="1">
                                  <p>
                                    <b>Unit code: </b>
                                    {item.courseCode}
                                  </p>
                                  <p>
                                    <b>Course ID: </b>
                                    {item.courseId}
                                  </p>
                                </Panel>
                              </Collapse>
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <div style={{ width: '5%', margin: '5px' }}>
                        <MinusCircleOutlined
                          onClick={() => remove(field.name)}
                        />
                      </div>
                    </div>
                    {type === 'Update' && currentUnits[field.name] && (
                      <>
                        <div style={{ marginTop: '-12px' }}>
                          {getUnitLabelText(field.name)}
                        </div>
                        <hr />
                      </>
                    )}
                  </>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    style={{ width: '50%' }}
                    onClick={() => {
                      add();
                    }}
                    icon={<PlusOutlined />}>
                    Add unit
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </section>

        <Divider />
        {(type === 'Update' || courseCode !== '') && (
          <>
            <Text style={{ display: 'flex', marginBottom: 8 }}>
              Different images! Don't forget to provide Height/Width!
            </Text>
            <Collapse>
              <Panel
                header="Top-left image in learning portal (Nav Bar Image)(M:120x120, D:240x240)  - mobile + desktop"
                key="2">
                <section>
                  <ImageUploadSection
                    beforeImageUpload={beforeImageUpload}
                    nest={['learnPortalNavbarImgData']}
                    // Need to insert name of course here `ssr-academy/${courseName}/cohort/png/`
                    path={`na-portal/learnSideNavImages/${courseCode}/`}
                    bucket={'image-assets.nasdaily.com'}
                    setStatus={setStatus}
                    form={form}
                    imgRequired={{ mobile: false, desktop: false }}
                    isDesktopInput={true}
                    showWebP={false}
                  />
                </section>
              </Panel>
            </Collapse>
            <Collapse>
              <Panel
                header="Thumbnail in courses dropdown, in learning portal  (M:180x180) - mobile only"
                key="3">
                <section>
                  <ImageUploadSection
                    beforeImageUpload={beforeImageUpload}
                    nest={['thumbnailImgData']}
                    // Need to insert name of course here `ssr-academy/${courseName}/cohort/png/`
                    path={`na-website/CourseThumbnails/${courseCode}/`}
                    bucket={'image-assets.nasdaily.com'}
                    setStatus={setStatus}
                    imgRequired={{ mobile: false, desktop: false }}
                    form={form}
                    showWebP={true}
                  />
                </section>
              </Panel>
            </Collapse>
            <Collapse>
              <Panel
                header="Degree Image (old thumbnail) (M:300x340) - mobile only"
                key="4">
                <section>
                  <ImageUploadSection
                    beforeImageUpload={beforeImageUpload}
                    nest={['imgData']}
                    // Need to insert name of course here `ssr-academy/${courseName}/cohort/png/`
                    path={`na-website/CourseCardImages/${courseCode}/`}
                    bucket={'image-assets.nasdaily.com'}
                    setStatus={setStatus}
                    imgRequired={{ mobile: false, desktop: false }}
                    form={form}
                    showWebP={true}
                  />
                </section>
              </Panel>
            </Collapse>
            <Collapse>
              <Collapse>
                <Panel
                  header="Search image in search dropdown (180x180) - mobile only"
                  key="3">
                  <section>
                    <ImageUploadSection
                      beforeImageUpload={beforeImageUpload}
                      nest={['searchImgData']}
                      path={`search/thumbnails`}
                      bucket={'image-assets.nasdaily.com'}
                      setStatus={setStatus}
                      hideResolution={{ desktop: true, mobile: false }}
                      imgRequired={{ mobile: false, desktop: false }}
                      form={form}
                      showWebP={false}
                    />
                  </section>
                </Panel>
              </Collapse>
              <Panel
                header="Discovery Card Image (307x172) - mobile only"
                key="2">
                <section>
                  <ImageUploadSection
                    beforeImageUpload={beforeImageUpload}
                    nest={['discoveryCardImgData']}
                    // Need to insert name of course here `ssr-academy/${courseName}/cohort/png/`
                    path={`na-website/DiscoveryPage/horizontal-thumbnails/`}
                    bucket={'image-assets.nasdaily.com'}
                    form={form}
                    hideResolution={{ desktop: true, mobile: false }}
                    imgRequired={{ mobile: false, desktop: false }}
                    showWebP={false}
                  />
                </section>
              </Panel>
            </Collapse>
            <Collapse>
              <Panel
                header="Avatar Image in discovery page(M:56x56,D:40x40) - M+D"
                key="2">
                <section>
                  <ImageUploadSection
                    beforeImageUpload={beforeImageUpload}
                    nest={['avatarImgData']}
                    path={`na-website/DiscoveryPage/v2/`}
                    bucket={'image-assets.nasdaily.com'}
                    form={form}
                    hideResolution={{ desktop: false, mobile: false }}
                    imgRequired={{ mobile: false, desktop: false }}
                    isDesktopInput={true}
                    showWebP={false}
                  />
                </section>
              </Panel>
            </Collapse>
            <Divider />
            <Text style={{ display: 'flex', marginBottom: 8 }}>
              The Subunit-cards that appear in the learning portal. Fix
              their position
            </Text>
            <Form.List name={['customCardMetadata']}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKey, ...restField }) => (
                    <Space
                      key={key}
                      style={{ display: 'flex', marginBottom: 8 }}
                      direction="vertical">
                      <Form.Item
                        label="Type"
                        name={[name, 'type']}
                        fieldKey={[fieldKey, 'type']}
                        {...restField}
                        rules={[
                          {
                            required: true,
                            message: 'Please choose a type'
                          }
                        ]}>
                        <Select
                          onChange={(e) => {
                            setSelectedCustomCards({
                              ...selectedCustomCards,
                              [name]: e
                            });
                          }}>
                          {customCards.map((customCard) => {
                            return (
                              <Select.Option value={customCard}>
                                {customCard}
                              </Select.Option>
                            );
                          })}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label="Day (sectionNumber)"
                        name={[name, 'sectionNumber']}
                        fieldKey={[fieldKey, 'sectionNumber']}
                        rules={[
                          {
                            required: false,
                            message: 'Please input section number like 0'
                          }
                        ]}>
                        <InputNumber min={0} />
                      </Form.Item>
                      <Form.Item
                        label="Position in Day (position)"
                        name={[name, 'position']}
                        fieldKey={[fieldKey, 'position']}
                        rules={[
                          {
                            required: false,
                            message: 'Please input position like 1 or 2'
                          }
                        ]}>
                        <InputNumber min={0} />
                      </Form.Item>
                      {cusotmLiveSessionCards.includes(
                        selectedCustomCards[name]
                      ) && (
                        <Form.Item
                          label="Number of live sessions"
                          name={[name, 'liveSessionCount']}
                          fieldKey={[fieldKey, 'liveSessionCount']}
                          rules={[
                            {
                              required: true,
                              message: 'submit for live sessions only!'
                            }
                          ]}>
                          <InputNumber
                            min={0}
                            placeholder="submit for live sessions only!"
                          />
                        </Form.Item>
                      )}
                      <ImageUploadSection
                        beforeImageUpload={beforeImageUpload}
                        nest={[name, 'data', 'imgData']}
                        // Need to insert name of course here `ssr-academy/${courseName}/cohort/png/`
                        path={`na-portal/${courseCode}/cohort/`}
                        bucket={'image-assets.nasdaily.com'}
                        setStatus={setStatus}
                        form={form}
                        // Only need to pass in if in a form list
                        listName="customCardMetadata"
                        imgRequired={{ mobile: false, desktop: false }}
                        hideResolution={{ mobile: true, desktop: true }}
                        isDesktopInput={true}
                      />
                      <MinusCircleOutlined onClick={() => remove(name)} />
                      <Divider />
                    </Space>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}>
                      Add custom card
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </>
        )}

        <Divider />
        <Collapse>
          <Panel header="Optional Fields" key="1">
            <Text strong="true">Details</Text>
            <section>
              <Form.Item
                label="Duration (weeks)"
                name="duration"
                placeholder={4}>
                <Input />
              </Form.Item>
              <Form.Item
                label="Class Time (hours)"
                name="classTime"
                placeholder={20}>
                <Input />
              </Form.Item>
              <Form.Item
                label="Video time"
                name="videoTime"
                placeholder="Make 3 videos">
                <Input />
              </Form.Item>
            </section>

            <Divider />
            <Text strong="true">Price</Text>
            <section>
              <Row justify="start">
                <Col span={10}>
                  <Form.Item label="Price" name="price">
                    <InputNumber />
                  </Form.Item>
                </Col>
                <Col span={14}>
                  <Form.Item label="Currency" name="currency">
                    <Select>
                      <Select.Option value="USD">USD</Select.Option>
                      <Select.Option value="SGD">SGD</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </section>

            <Divider />
            <Text strong="true">Course Benefits</Text>
            <section>
              <Form.List name="benefits">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Form.Item
                        {...(index === 0
                          ? formItemLayout
                          : formItemLayoutWithOutLabel)}
                        label={index === 0 ? 'Benefits' : ''}
                        required={false}
                        key={field.key}>
                        <Form.Item
                          {...field}
                          validateTrigger={['onChange', 'onBlur']}
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message:
                                'Please input one benefit or delete this field.'
                            }
                          ]}
                          noStyle>
                          <Input
                            placeholder="Add benefits"
                            style={{ width: '60%' }}
                          />
                        </Form.Item>
                        <MinusCircleOutlined
                          className="dynamic-delete-button"
                          onClick={() => remove(field.name)}
                        />
                      </Form.Item>
                    ))}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        style={{ width: '60%' }}
                        icon={<PlusOutlined />}>
                        Add field after
                      </Button>
                      <Button
                        type="dashed"
                        onClick={() => {
                          add('Field before', 0);
                        }}
                        style={{ width: '60%', marginTop: '20px' }}
                        icon={<PlusOutlined />}>
                        Add field before
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </section>
            <Divider />
          </Panel>
        </Collapse>

        {status.type === 'ERROR' ? (
          <Text type="danger">{status.message}</Text>
        ) : null}
        {status.type === 'SUCCESS' ? (
          <Text type="success">Successful!</Text>
        ) : null}
        <br />
        <Row justify="end" gutter={12}>
          <Col>
            <Button type="primary" htmlType="submit">
              {type}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CourseForm;
